import { createNewMessage } from 'bundles/ai-coach-platform/components/patterns/chat/utils/messageUtils';

import { AICOACH_FEEDBACK, MESSAGE_SENDER } from '../../constants';
import {
  ADD_NEW_RESPONSE_MESSAGE,
  ADD_NEW_USER_MESSAGE,
  ADD_STREAMED_RESPONSE_MESSAGE,
  DELETE_MESSAGES,
  DROP_MESSAGES,
  FEEDBACK_SET_LIKE,
  FEEDBACK_SET_UNLIKE,
  FEEDBACK_UNSET,
  HIDE_AVATAR,
  PREPEND_CHAT_HISTORY,
  SET_CHAT_SESSION_ID,
  UPDATE_CHAT_HISTORY,
} from '../actions/types';
import type { MessagesActions, ResponseMessage, UpdateChatHistory, UserMessage } from '../actions/types';
import type { EmbeddedContents, MessagesState } from '../types';
import { createReducer } from '../utils/createReducer';

type Message = UserMessage | ResponseMessage;

export type InitialState = {
  messages: Array<Message>;
  chatSessionId: string | null;
  messagesBySessionId: Record<string, Array<Message>>;
  streamedMessage: string | null;
  embeddedContents: EmbeddedContents | null;
};

const initialState: InitialState = {
  messages: [],
  chatSessionId: null,
  messagesBySessionId: {},
  streamedMessage: null,
  embeddedContents: null,
};

const messagesReducer = {
  [UPDATE_CHAT_HISTORY]: (
    state: MessagesState,
    { messages, chatSessionId }: { messages: UpdateChatHistory['messages']; chatSessionId?: string }
  ) => {
    const formattedMessages = messages.map((message) => {
      return createNewMessage(message);
    });
    const targetChatSessionId = chatSessionId ?? state.chatSessionId;
    // Some flows fetch history for a session before selecting it as active.
    // Cache targeted fetches by session id, but only replace the visible fallback
    // messages list when the fetched session is already active.
    const shouldUpdateActiveMessages = chatSessionId === undefined || chatSessionId === state.chatSessionId;

    return {
      ...state,
      messages: shouldUpdateActiveMessages ? [...formattedMessages] : state.messages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(targetChatSessionId ? { [targetChatSessionId]: formattedMessages } : {}),
      },
    };
  },

  [ADD_NEW_USER_MESSAGE]: (state: MessagesState, { text, id, timestamp, action, courseraContext }: UserMessage) => {
    const messages = [
      ...state.messages,
      createNewMessage({ text, sender: MESSAGE_SENDER.CLIENT, id, timestamp, action, courseraContext }),
    ];

    return {
      ...state,
      messages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(state.chatSessionId ? { [state.chatSessionId]: messages } : {}),
      },
    };
  },

  [ADD_NEW_RESPONSE_MESSAGE]: (
    state: MessagesState,
    {
      text,
      id,
      userFeedback,
      timestamp,
      navigationButtons,
      structuredResponse,
      embeddedContents,
      recommendedActions,
      courseraContext,
      recommendedEdits,
      productCards,
    }: ResponseMessage
  ) => {
    const messages = [
      ...state.messages,
      createNewMessage({
        text,
        sender: MESSAGE_SENDER.RESPONSE,
        id,
        userFeedback,
        timestamp,
        navigationButtons,
        structuredResponse,
        embeddedContents,
        recommendedActions,
        courseraContext,
        recommendedEdits,
        productCards,
      }),
    ];

    return {
      ...state,
      messages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(state.chatSessionId ? { [state.chatSessionId]: messages } : {}),
      },
    };
  },

  [ADD_STREAMED_RESPONSE_MESSAGE]: (
    state: MessagesState,
    { text, embeddedContents }: { text: string | null; embeddedContents?: EmbeddedContents | null }
  ) => {
    if (text == null) {
      return { ...state, streamedMessage: null, embeddedContents: null };
    }
    return {
      ...state,
      streamedMessage: text,
      embeddedContents: embeddedContents ?? state.embeddedContents,
    };
  },

  [PREPEND_CHAT_HISTORY]: (
    state: MessagesState,
    { messages, chatSessionId }: { messages: UpdateChatHistory['messages']; chatSessionId?: string }
  ) => {
    const messagesToPrepend = messages.map((message) => {
      return createNewMessage(message);
    });

    const targetChatSessionId = chatSessionId ?? state.chatSessionId;
    const currentSessionMessages = targetChatSessionId ? state.messagesBySessionId[targetChatSessionId] || [] : [];
    // Keep pagination for inactive sessions scoped to the per-session cache.
    // SET_CHAT_SESSION_ID hydrates the visible messages list from that cache when
    // the session becomes active.
    const shouldUpdateActiveMessages = chatSessionId === undefined || chatSessionId === state.chatSessionId;

    return {
      ...state,
      messages: shouldUpdateActiveMessages ? [...messagesToPrepend, ...state.messages] : state.messages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(targetChatSessionId ? { [targetChatSessionId]: [...messagesToPrepend, ...currentSessionMessages] } : {}),
      },
    };
  },

  [DROP_MESSAGES]: (state: MessagesState) => ({
    ...state,
    messages: [],
    messagesBySessionId: {
      ...state.messagesBySessionId,
      ...(state.chatSessionId ? { [state.chatSessionId]: [] } : {}),
    },
  }),

  [HIDE_AVATAR]: (state: MessagesState, { index }: { index: number }) => {
    const newMessages = [...state.messages];

    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    newMessages[index].showAvatar = false;

    const messagesForCurrentSession = state.chatSessionId ? state.messagesBySessionId[state.chatSessionId] : [];

    if (messagesForCurrentSession) {
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      messagesForCurrentSession[index].showAvatar = false;
    }

    return {
      ...state,
      messages: newMessages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(state.chatSessionId ? { [state.chatSessionId]: messagesForCurrentSession } : {}),
      },
    };
  },
  /**
   * The following function deletes a certain number of messages, where the
   * number of messages to delete is the input `count`.
   *
   * When the input `id` is available, the last message to delete is the one
   * whose customId matches `id`; otherwise, the last message to delete is the
   * last message in the list
   */
  [DELETE_MESSAGES]: (state: MessagesState, { count, id }: { count: number; id: string }) => {
    const remainingMessages = id
      ? state.messages.filter((_, index) => {
          const targetMsgIndex = state.messages.findIndex((tMsg) => tMsg.customId === id);
          return index < targetMsgIndex - count + 1 || index > targetMsgIndex;
        })
      : state.messages.slice(0, state.messages.length - count);

    return {
      ...state,
      messages: remainingMessages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(state.chatSessionId ? { [state.chatSessionId]: remainingMessages } : {}),
      },
    };
  },

  [FEEDBACK_SET_LIKE]: (state: MessagesState, { id }: { id: string }) => {
    const messages = state.messages.map((message) => ({
      ...message,
      userFeedback: id === message.customId ? AICOACH_FEEDBACK.like : message.userFeedback,
    }));

    return {
      ...state,
      messages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(state.chatSessionId ? { [state.chatSessionId]: messages } : {}),
      },
    };
  },

  [FEEDBACK_SET_UNLIKE]: (state: MessagesState, { id }: { id: string }) => {
    const messages = state.messages.map((message) => ({
      ...message,
      userFeedback: id === message.customId ? AICOACH_FEEDBACK.dislike : message.userFeedback,
    }));

    return {
      ...state,
      messages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(state.chatSessionId ? { [state.chatSessionId]: messages } : {}),
      },
    };
  },

  [FEEDBACK_UNSET]: (state: MessagesState, { id }: { id: string }) => {
    const messages = state.messages.map((message) => ({
      ...message,
      userFeedback: id === message.customId ? AICOACH_FEEDBACK.unset : message.userFeedback,
    }));

    return {
      ...state,
      messages,
      messagesBySessionId: {
        ...state.messagesBySessionId,
        ...(state.chatSessionId ? { [state.chatSessionId]: messages } : {}),
      },
    };
  },

  [SET_CHAT_SESSION_ID]: (state: MessagesState, { chatSessionId }: { chatSessionId: string | null }) => ({
    ...state,
    chatSessionId,
    ...(chatSessionId && state.messagesBySessionId[chatSessionId]
      ? { messages: state.messagesBySessionId[chatSessionId] }
      : {}),
  }),
};

export default (state = initialState, action: MessagesActions) => createReducer(messagesReducer, state, action);
